/* @refresh reset */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  PlusIcon,
  ShieldExclamationIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { PMButton } from "../../utils/PMButton";
import { patchCompanyAiMailbox } from "../../../api/microsoft";
import { AiInboxRoutingForm } from "./AiInboxRoutingForm";
import {
  buildAiRoutingPayload,
  normalizeAiRouting,
} from "./aiInboxRoutingUtils";
import { PromangRichTextEditor } from "../../utils/PromangRichTextEditor";

const MAIL_SORT_OPTIONS = [
  { value: "received_desc", labelKey: "microsoft.ai_mailboxes.settings.sort_received_desc" },
  { value: "received_asc", labelKey: "microsoft.ai_mailboxes.settings.sort_received_asc" },
  { value: "subject_asc", labelKey: "microsoft.ai_mailboxes.settings.sort_subject_asc" },
];

const MATCH_TYPES = [
  { value: "subject_contains", labelKey: "microsoft.ai_mailboxes.settings.match_subject_contains" },
  { value: "subject_equals", labelKey: "microsoft.ai_mailboxes.settings.match_subject_equals" },
  { value: "from_domain", labelKey: "microsoft.ai_mailboxes.settings.match_from_domain" },
  { value: "from_contains", labelKey: "microsoft.ai_mailboxes.settings.match_from_contains" },
];

const RULE_ACTIONS = [
  { value: "skip_case", labelKey: "microsoft.ai_mailboxes.settings.action_skip_case" },
  { value: "forward_to", labelKey: "microsoft.ai_mailboxes.settings.action_forward_to" },
];

function normalizeExclusions(ex) {
  const x = ex && typeof ex === "object" ? ex : {};
  return {
    skip_promotional_hint: Boolean(x.skip_promotional_hint),
    subject_contains_ignore: Array.isArray(x.subject_contains_ignore)
      ? x.subject_contains_ignore
      : [],
    from_domains_ignore: Array.isArray(x.from_domains_ignore) ? x.from_domains_ignore : [],
  };
}

function exclusionsToTextareas(ex) {
  return {
    subjects: (ex.subject_contains_ignore || []).join(", "),
    domains: (ex.from_domains_ignore || []).join(", "),
  };
}

function parseList(text) {
  return String(text || "")
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function normalizeRules(rules) {
  if (!Array.isArray(rules)) return [];
  return rules.map((r) => {
    const ap = r?.action_params && typeof r.action_params === "object" ? r.action_params : {};
    return {
      name: r?.name || "",
      action: r?.action || "skip_case",
      match_type: r?.match_type || "subject_contains",
      match_value: r?.match_value || "",
      enabled: r?.enabled !== false,
      action_params: { to: String(ap.to || "").trim() },
    };
  });
}

/**
 * Per-mailbox processing settings (saved via PATCH /microsoft/company/ai_mailboxes/:id).
 * Optional +patchMailbox(payload)+ for personal mail (PATCH /microsoft/me/mail_ai/settings).
 */
export function CompanyAiMailboxSettingsPanel({ mailbox, onSaved, patchMailbox }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [mailSort, setMailSort] = useState("received_desc");
  const [autoCreateCase, setAutoCreateCase] = useState(true);
  const [archiveAfter, setArchiveAfter] = useState(false);
  const [autoReply, setAutoReply] = useState(false);
  const [autoReplyBody, setAutoReplyBody] = useState("");
  const [signatureHtml, setSignatureHtml] = useState("");
  const [skipPromo, setSkipPromo] = useState(false);
  const [subjectsText, setSubjectsText] = useState("");
  const [domainsText, setDomainsText] = useState("");
  const [rules, setRules] = useState([]);
  const [jobEnabled, setJobEnabled] = useState(false);
  const [aiRoutingForm, setAiRoutingForm] = useState(() => normalizeAiRouting(null));
  const [aiRoutingJsonMode, setAiRoutingJsonMode] = useState(false);
  const [aiRoutingJsonText, setAiRoutingJsonText] = useState("{}");

  useEffect(() => {
    if (!mailbox?.id) return;
    setMailSort(mailbox.mail_sort || "received_desc");
    setAutoCreateCase(mailbox.auto_create_case_enabled !== false);
    setArchiveAfter(Boolean(mailbox.archive_after_processing));
    setAutoReply(Boolean(mailbox.auto_reply_enabled));
    setAutoReplyBody(mailbox.auto_reply_body_html || "");
    setSignatureHtml(mailbox.signature_html || "");
    const ex = normalizeExclusions(mailbox.auto_create_case_exclusions);
    setSkipPromo(ex.skip_promotional_hint);
    const tx = exclusionsToTextareas(ex);
    setSubjectsText(tx.subjects);
    setDomainsText(tx.domains);
    setRules(normalizeRules(mailbox.processing_rules));
    setJobEnabled(Boolean(mailbox.inbox_processing_job_enabled));
    const air = normalizeAiRouting(mailbox.ai_inbox_routing);
    setAiRoutingForm(air);
    try {
      setAiRoutingJsonText(JSON.stringify(buildAiRoutingPayload(air), null, 2));
    } catch {
      setAiRoutingJsonText("{}");
    }
  }, [mailbox]);

  const payload = useMemo(
    () => ({
      mail_sort: mailSort,
      inbox_processing_job_enabled: jobEnabled,
      auto_create_case_enabled: autoCreateCase,
      archive_after_processing: archiveAfter,
      auto_reply_enabled: autoReply,
      auto_reply_subject: null,
      auto_reply_body_html: autoReplyBody || null,
      signature_html: signatureHtml || null,
      auto_create_case_exclusions: {
        skip_promotional_hint: skipPromo,
        subject_contains_ignore: parseList(subjectsText),
        from_domains_ignore: parseList(domainsText),
      },
      processing_rules: rules.map((r) => {
        const row = {
          name: r.name,
          action: r.action,
          match_type: r.match_type,
          match_value: r.match_value,
          enabled: r.enabled,
        };
        if (r.action === "forward_to") {
          row.action_params = {
            to: String(r.action_params?.to ?? "").trim(),
          };
        }
        return row;
      }),
    }),
    [
      mailSort,
      jobEnabled,
      autoCreateCase,
      archiveAfter,
      autoReply,
      autoReplyBody,
      signatureHtml,
      skipPromo,
      subjectsText,
      domainsText,
      rules,
    ]
  );

  const addRule = () => {
    setRules((prev) => [
      ...prev,
      {
        name: "",
        action: "skip_case",
        match_type: "subject_contains",
        match_value: "",
        enabled: true,
        action_params: { to: "" },
      },
    ]);
  };

  const updateRule = (idx, patch) => {
    setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRule = (idx) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!mailbox?.id) return;
    let aiParsed = {};
    if (aiRoutingJsonMode) {
      try {
        aiParsed = JSON.parse(
          aiRoutingJsonText && aiRoutingJsonText.trim() ? aiRoutingJsonText : "{}"
        );
      } catch {
        toast.error(t("microsoft.ai_mailboxes.settings.ai_routing_invalid_json"));
        return;
      }
    } else {
      aiParsed = buildAiRoutingPayload(aiRoutingForm);
    }
    setSaving(true);
    try {
      const savePayload = { ...payload, ai_inbox_routing: aiParsed };
      const data = patchMailbox
        ? await patchMailbox(savePayload)
        : await patchCompanyAiMailbox(mailbox.id, savePayload);
      toast.success(t("microsoft.ai_mailboxes.settings.saved"));
      const updated = patchMailbox ? data?.mail_ai_settings : data?.ai_mailbox;
      onSaved?.(updated);
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.[0] ||
        t("microsoft.ai_mailboxes.settings.save_failed");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 border-t border-gray-200 bg-white px-3 py-4 sm:px-4">
      <div
        className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-950 ring-1 ring-amber-100"
        role="status"
      >
        <ShieldExclamationIcon
          className="h-5 w-5 shrink-0 text-amber-700"
          aria-hidden
        />
        <p className="leading-relaxed">{t("microsoft.ai_mailboxes.mail_safety_dev_notice")}</p>
      </div>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {t("microsoft.ai_mailboxes.settings.heading")}
      </p>
      <p className="text-xs text-gray-600">{t("microsoft.ai_mailboxes.settings.intro")}</p>

      <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              {t("microsoft.ai_mailboxes.settings.processing_job_title")}
            </p>
            <p className="mt-1 text-xs text-gray-600">
              {t("microsoft.ai_mailboxes.settings.processing_job_hint")}
            </p>
          </div>
          <div
            className="inline-flex shrink-0 rounded-lg border border-gray-300 bg-white p-0.5 shadow-sm"
            role="group"
            aria-label={t("microsoft.ai_mailboxes.settings.processing_job_title")}
          >
            <button
              type="button"
              onClick={() => setJobEnabled(false)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                !jobEnabled
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {t("microsoft.ai_mailboxes.settings.processing_job_off")}
            </button>
            <button
              type="button"
              onClick={() => setJobEnabled(true)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                jobEnabled
                  ? "bg-emerald-600 text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {t("microsoft.ai_mailboxes.settings.processing_job_on")}
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-700">
            {t("microsoft.ai_mailboxes.settings.mail_sort")}
          </label>
          <select
            value={mailSort}
            onChange={(e) => setMailSort(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          >
            {MAIL_SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.labelKey)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50/90 px-3 py-2.5 text-xs text-slate-800">
        <p className="font-medium text-slate-900">
          {t("microsoft.ai_mailboxes.settings.case_filters_replaced_title")}
        </p>
        <p className="mt-1 leading-relaxed text-slate-700">
          {t("microsoft.ai_mailboxes.settings.case_filters_replaced_body")}
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={archiveAfter}
          onChange={(e) => setArchiveAfter(e.target.checked)}
          className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
        />
        {t("microsoft.ai_mailboxes.settings.archive_after_processing")}
      </label>
      <p className="text-xs text-gray-500">
        {t("microsoft.ai_mailboxes.settings.archive_hint")}
      </p>

      <div className="space-y-2 rounded-lg border border-gray-100 p-3">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
          <input
            type="checkbox"
            checked={autoReply}
            onChange={(e) => setAutoReply(e.target.checked)}
            className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
          />
          {t("microsoft.ai_mailboxes.settings.auto_reply_enabled")}
        </label>
        <textarea
          value={autoReplyBody}
          onChange={(e) => setAutoReplyBody(e.target.value)}
          rows={4}
          disabled={!autoReply}
          placeholder={t("microsoft.ai_mailboxes.settings.auto_reply_body_ph")}
          className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:bg-gray-100"
        />
        <p className="text-xs text-gray-500">{t("microsoft.ai_mailboxes.settings.auto_reply_note")}</p>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700">
          {t("microsoft.ai_mailboxes.settings.signature_html")}
        </label>
        <div className="mt-1">
          <PromangRichTextEditor
            value={signatureHtml}
            onChange={setSignatureHtml}
            placeholder={t("microsoft.ai_mailboxes.settings.signature_ph")}
            size="sm"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-gray-800">
            {t("microsoft.ai_mailboxes.settings.rules_heading")}
          </p>
          <button
            type="button"
            onClick={addRule}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <PlusIcon className="h-4 w-4" />
            {t("microsoft.ai_mailboxes.settings.add_rule")}
          </button>
        </div>
        <p className="text-xs text-gray-500">{t("microsoft.ai_mailboxes.settings.rules_intro")}</p>
        {rules.length === 0 ? (
          <p className="text-xs text-gray-400">{t("microsoft.ai_mailboxes.settings.no_rules")}</p>
        ) : (
          <ul className="space-y-2">
            {rules.map((r, idx) => (
              <li
                key={idx}
                className="grid gap-2 rounded-lg border border-gray-200 bg-white p-2 sm:grid-cols-12 sm:items-end"
              >
                <div className="sm:col-span-3">
                  <label className="text-[10px] font-medium text-gray-500">
                    {t("microsoft.ai_mailboxes.settings.rule_name")}
                  </label>
                  <input
                    type="text"
                    value={r.name}
                    onChange={(e) => updateRule(idx, { name: e.target.value })}
                    className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-xs"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="text-[10px] font-medium text-gray-500">
                    {t("microsoft.ai_mailboxes.settings.rule_match")}
                  </label>
                  <select
                    value={r.match_type}
                    onChange={(e) => updateRule(idx, { match_type: e.target.value })}
                    className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-xs"
                  >
                    {MATCH_TYPES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {t(m.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-3">
                  <label className="text-[10px] font-medium text-gray-500">
                    {t("microsoft.ai_mailboxes.settings.rule_value")}
                  </label>
                  <input
                    type="text"
                    value={r.match_value}
                    onChange={(e) => updateRule(idx, { match_value: e.target.value })}
                    className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-xs"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[10px] font-medium text-gray-500">
                    {t("microsoft.ai_mailboxes.settings.rule_action")}
                  </label>
                  <select
                    value={r.action}
                    onChange={(e) => {
                      const action = e.target.value;
                      const patch = { action };
                      if (action === "forward_to") {
                        patch.action_params = {
                          to: r.action_params?.to || "",
                        };
                      }
                      updateRule(idx, patch);
                    }}
                    className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-xs"
                  >
                    {RULE_ACTIONS.map((a) => (
                      <option key={a.value} value={a.value}>
                        {t(a.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-end gap-2 sm:col-span-1">
                  <label className="flex items-center gap-1 text-[10px] text-gray-600">
                    <input
                      type="checkbox"
                      checked={r.enabled}
                      onChange={(e) => updateRule(idx, { enabled: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    {t("microsoft.ai_mailboxes.settings.rule_on")}
                  </label>
                  <button
                    type="button"
                    onClick={() => removeRule(idx)}
                    className="rounded p-1 text-rose-600 hover:bg-rose-50"
                    title={t("microsoft.ai_mailboxes.settings.remove_rule")}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                {r.action === "forward_to" ? (
                  <div className="sm:col-span-12">
                    <label className="text-[10px] font-medium text-gray-500">
                      {t("microsoft.ai_mailboxes.settings.rule_forward_to_email")}
                    </label>
                    <input
                      type="email"
                      autoComplete="email"
                      value={r.action_params?.to || ""}
                      onChange={(e) =>
                        updateRule(idx, {
                          action_params: { ...(r.action_params || {}), to: e.target.value },
                        })
                      }
                      placeholder={t("microsoft.ai_mailboxes.settings.rule_forward_to_placeholder")}
                      className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-xs"
                    />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-3 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
        <div>
          <p className="text-sm font-medium text-gray-900">
            {t("microsoft.ai_mailboxes.settings.ai_routing_heading")}
          </p>
          <p className="mt-1 text-xs text-gray-600">{t("microsoft.ai_mailboxes.settings.ai_routing_hint")}</p>
        </div>

        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-indigo-200/80 bg-white/80 px-3 py-2 text-xs font-medium text-gray-800">
          <input
            type="checkbox"
            checked={aiRoutingJsonMode}
            onChange={(e) => {
              const on = e.target.checked;
              if (on) {
                setAiRoutingJsonText(
                  JSON.stringify(buildAiRoutingPayload(aiRoutingForm), null, 2)
                );
              } else {
                try {
                  const parsed = JSON.parse(
                    aiRoutingJsonText.trim() ? aiRoutingJsonText : "{}"
                  );
                  setAiRoutingForm(normalizeAiRouting(parsed));
                } catch {
                  toast.error(t("microsoft.ai_mailboxes.settings.ai_routing_invalid_json"));
                  return;
                }
              }
              setAiRoutingJsonMode(on);
            }}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          {t("microsoft.ai_mailboxes.settings.ai_routing_raw_json")}
        </label>
        <p className="text-[10px] text-gray-500">
          {t("microsoft.ai_mailboxes.settings.ai_routing_raw_json_hint")}
        </p>

        {aiRoutingJsonMode ? (
          <textarea
            value={aiRoutingJsonText}
            onChange={(e) => setAiRoutingJsonText(e.target.value)}
            rows={14}
            spellCheck={false}
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        ) : (
          <AiInboxRoutingForm
            value={aiRoutingForm}
            onChange={setAiRoutingForm}
            disabled={saving}
          />
        )}
      </div>

      <div className="flex justify-end pt-2">
        <PMButton
          type="button"
          styleType="primary"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? t("microsoft.status.loading") : t("microsoft.ai_mailboxes.settings.save")}
        </PMButton>
      </div>
    </div>
  );
}
